<template>
  <content-modal
    class="message-modal otp-verification-modal otp"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    @modal-shown="actionShown"
    @modal-hidden="actionHidden"
    @modal-hide-request="actionHideRequest"
  >
    <template v-if="use_title" #title>{{ params.title }}</template>
    <template v-if="use_content" #content>
      <modal-content>
        <template #content>
          <div class="instruction">
            {{ params.content }}
          </div>
          <cv-form @submit.prevent>
            <dl class="field-set type-text input-code">
              <dt class="field-label">{{ t("email_otp_verification_code_label") }}</dt>
              <dd class="field-input">
                <text-input
                  v-model:value="otp"
                  :error-message="errorMessage"
                  @input="handleUpdateOTP"
                />
              </dd>
            </dl>
            <dl class="field-set type-button generate-code">
              <dt class="field-label">{{ t("email_otp_regenerate_code_label") }}</dt>
              <dd v-if="showCountDownMessage" class="field-message">
                <i18n-t
                  keypath="email_opt_verification_modal_countdown_message_label"
                  class="countdown"
                >
                  <template #timer>
                    <span>{{ timer }}</span>
                  </template>
                </i18n-t>
              </dd>
              <dd v-else class="field-input">
                <link-button @click="handleResendOtp()">
                  {{ t("email_opt_verification_modal_regenerate_code_label") }}
                </link-button>
              </dd>
            </dl>
          </cv-form>
        </template>
        <template #tools>
          <cv-button
            v-if="use_secondaryButton"
            class="cancel"
            kind="tertiary"
            size="field"
            @click="actionSecondary"
            >{{ t("button_cancel") }}</cv-button
          >
          <fetch-data-button
            v-if="use_primaryButton"
            v-bind="dataProceedButton"
            :disabled="primaryButtonDisabled"
            @click="actionPrimary"
            @reset-error-state="handleSubmitButtonResetError"
          >
            <span>{{ t("button_proceed") }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import DateTimeUtils from "@utils/datetime.utils";
import modalEventBus from "@plugins/modal/service/event-bus";
import appConfigs from "@configs/app";
import LinkButton from "@plugins/common/components/LinkButton.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import TextInput from "@plugins/common/components/TextInput.vue";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import { OTPParams } from "@plugins/otp/types.ts";

defineOptions({ name: "OTPVerificationModal" });

const { t } = useI18n();

const props = defineProps<{ params: OTPParams }>();

const otp = ref("");
const closeAriaLabel = ref("Close");
const use_title = ref(true);
const use_content = ref(true);
const size = ref("small");
const use_secondaryButton = ref(true);
const use_primaryButton = ref(true);
const primaryButtonDisabled = ref(false);
const autoHideOff = ref(true);
const visible = ref(true);
const countdown = ref(0);
const errorMessage = ref("");
const showCountDownMsg = ref(false);

const dataProceedButton = ref<{
  kind: string;
  size: string;
  error: boolean;
  loading: boolean;
  completed: boolean;
  errorMessage?: string;
}>({
  kind: "primary",
  size: "field",
  error: false,
  loading: false,
  completed: false,
});

const showCountDownMessage = computed(() => countdown.value > 0 && showCountDownMsg.value);

const countdownInMinutes = computed(() => {
  const minute = "0" + DateTimeUtils.getMinutesOnly(countdown.value).toString().slice(-2);
  let second = DateTimeUtils.getSeconds(countdown.value).toString().slice(-2);
  if (second.length < 2) {
    second = "0" + DateTimeUtils.getSeconds(countdown.value).toString().slice(-2);
  }
  return `${minute}:${second}`;
});

const timer = computed(() => countdownInMinutes.value);

const APIService = {
  sendOTP: props.params.sendOTP,
  resendOTP: props.params.resendOTP,
  verify: props.params.verifyOTP,
};

let intervalTimer: number | undefined;

function sendOTP() {
  primaryButtonDisabled.value = true;
  dataProceedButton.value.loading = true;
  APIService.sendOTP(props.params.email).then((response) => {
    if (response.code !== "ok") {
      primaryButtonDisabled.value = false;
      dataProceedButton.value.loading = false;
      dataProceedButton.value.error = true;
      return;
    }
    primaryButtonDisabled.value = false;
    dataProceedButton.value.loading = false;
    resetCountDown();
    showCountDownMsg.value = true;
  });
}

function handleResendOtp() {
  showCountDownMsg.value = false;
  primaryButtonDisabled.value = true;
  dataProceedButton.value.loading = true;

  APIService.resendOTP(props.params.email).then(() => {
    primaryButtonDisabled.value = false;
    dataProceedButton.value.loading = false;
    resetCountDown();
    showCountDownMsg.value = true;
  });
}

async function verifyOTP(): Promise<boolean> {
  if (otp.value.length !== 6) {
    errorMessage.value = t("email_opt_verification_modal_error_message_label");
    dataProceedButton.value.error = true;
    dataProceedButton.value.errorMessage = "";
    return false;
  }
  const otpParam = { otp: otp.value };

  dataProceedButton.value.loading = true;
  primaryButtonDisabled.value = true;

  const response = await APIService.verify(otpParam);
  dataProceedButton.value.loading = false;
  primaryButtonDisabled.value = false;

  if (response.code && response.code !== "ok") {
    errorMessage.value = t("email_opt_verification_modal_error_message_label");
    dataProceedButton.value.error = true;
    dataProceedButton.value.errorMessage = "";
    return false;
  }
  props.params.actionPrimary();
  visible.value = false;
  return true;
}

function handleUpdateOTP(nextOtp: string) {
  otp.value = nextOtp;
  errorMessage.value = "";
}

function resetCountDown() {
  countdown.value = appConfigs.otpCountdownInterval;
  if (intervalTimer) window.clearInterval(intervalTimer);
  intervalTimer = window.setInterval(() => {
    countdown.value -= 1;
    if (countdown.value === 0 && intervalTimer) window.clearInterval(intervalTimer);
  }, 1000);
}

function actionShown() {
  visible.value = true;
}

function actionHidden() {
  visible.value = false;
  const instance = getCurrentInstance();
  modalEventBus.$emit("modal-hidden", instance?.vnode.key);
}

function actionPrimary() {
  verifyOTP();
}

function actionSecondary() {
  props.params.actionSecondary();
  visible.value = false;
}

function actionHideRequest(event: Record<string, unknown>) {
  if (event["cv:reason"] !== "external-click") {
    props.params.actionSecondary();
    visible.value = false;
  }
}

function handleSubmitButtonResetError() {
  dataProceedButton.value.error = false;
}

onMounted(() => {
  if (props.params.requireSendOTP) {
    sendOTP();
  } else {
    resetCountDown();
    showCountDownMsg.value = true;
  }
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/typo";

.bx--modal.otp-verification-modal {
  .bx--modal-content {
    // Higher specificity to override generic styling in bx--modal
    .link-button {
      @include carbon--type-style("button-01", true);
    }
  }
}
</style>
